import { useEffect, useState } from 'react'
import { Building2, ClipboardCheck, Minus, Plus, X } from 'lucide-react'
import { Button, ButtonGlyph, buttonClass, EndpointHint, FormError, HelpIcon, Link, LoadFailure, Skeleton } from '../../core/index.js'
import { useCensus, useCensusEvaluation, useCensusRequirements, useRecordCensus } from '../hooks.js'
import type { CensusUnitView, CensusView } from '../types.js'
import { CensusRequirementView } from './CensusRequirementView.js'
import { CensusEvaluationPanel } from './CensusEvaluationPanel.js'
import { CensusDecisions } from './CensusDecisions.js'
import { CensusTable } from './CensusTable.js'
import { MonthStepper } from './MonthStepper.js'
import { addDays, censusRange, describeRequirementFigure, rowFor, shortDay } from './censusCardData.js'
import { datesOf, describePeriod } from './preferencesText.js'
import { shiftMonth, todayDate } from './scheduleCalendarData.js'

const INPUT = 'w-20 rounded border border-marine/30 bg-white px-2 py-1 text-right text-sm font-bold text-ink focus:outline-none focus:border-marine'

/** The counts typed for today, by unit; blank means not entered. */
type TodayDraft = Record<string, string>

/** Each unit's box starts at today's recorded count, else today's forecast (to be confirmed by Record), else blank. */
function draftFrom(view: CensusView, today: string): TodayDraft {
  return Object.fromEntries(view.units.map((unit) => {
    const row = rowFor(view, today, unit.id)
    return [unit.id, row && row.census !== null ? String(row.census) : '']
  }))
}

function parseCount(text: string): number | null {
  const value = Number(text)
  return text.trim() !== '' && Number.isInteger(value) && value >= 0 ? value : null
}

/** One unit's line of the Today form: the name, the count right beside it, and whether today's count is recorded yet. */
function TodayRow({ unit, view, today, value, onChange }: { unit: CensusUnitView; view: CensusView; today: string; value: string; onChange: (text: string) => void }) {
  const yesterday = rowFor(view, addDays(today, -1), unit.id)
  const todayRow = rowFor(view, today, unit.id)
  const isRecorded = todayRow !== null && todayRow.census !== null && todayRow.basis === 'recorded' && String(todayRow.census) === value
  const hint = todayRow && todayRow.census !== null && todayRow.basis !== 'recorded' ? `forecast ${todayRow.census}` : yesterday && yesterday.census !== null ? `yesterday ${yesterday.census}` : null
  return (
    <label className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5">
      <span className="w-44 min-w-0 shrink-0">
        <span className="block text-xs font-bold text-ink">{unit.name}</span>
        <span className="block text-[10px] text-ink-mid">
          {unit.beds !== null ? `${unit.beds} beds` : 'beds not kept'}
          {hint && ` · ${hint}`}
        </span>
      </span>
      <input type="number" inputMode="numeric" min={0} max={unit.beds ?? 2000} aria-label={`Today's census on ${unit.name}`} className={INPUT} value={value} onChange={(event) => onChange(event.target.value)} placeholder="count" />
      {value.trim() !== '' && (
        <span className={`text-[10px] font-bold ${isRecorded ? 'text-green' : 'text-ink-mid'}`}>{isRecorded ? 'Recorded' : 'Not recorded yet'}</span>
      )}
    </label>
  )
}

/** The What-if for one date and unit: a count to try, what it would require, and Record to make it the expected census. */
function WhatIf({ facilityId, view, date, unitId, onClose, onRecorded, configHref }: { facilityId: string; view: CensusView; date: string; unitId: string; onClose: () => void; onRecorded: () => void; configHref?: string }) {
  const unit = view.units.find((entry) => entry.id === unitId)
  const row = rowFor(view, date, unitId)
  const [census, setCensus] = useState<number>(row && row.census !== null ? row.census : unit?.plannedCensus ?? 0)
  useEffect(() => setCensus(row && row.census !== null ? row.census : unit?.plannedCensus ?? 0), [date, unitId, row, unit?.plannedCensus])
  const requirements = useCensusRequirements(facilityId, { date, unitId, census })
  const evaluation = useCensusEvaluation(facilityId, { date, unitId, census })
  const record = useRecordCensus(facilityId)
  const answer = requirements.data?.units[0] ?? null
  const max = unit?.beds ?? 2000
  return (
    <div className="rounded border border-coral/40 bg-canvas/60 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-bold text-ink">
            What if {unit?.name ?? unitId} has <span className="text-coral">{census}</span> residents on {shortDay(date)}?
          </div>
          <div className="text-[10px] text-ink-mid">Now: {row && row.census !== null ? `${row.census}, ${describeRequirementFigure(row, date)}` : 'no count and nothing to forecast from'}. Nothing is recorded until you press Record.</div>
        </div>
        <button type="button" aria-label="Close the what-if" className="p-1 text-ink-mid hover:text-coral" onClick={onClose}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button icon={Minus} variant="secondary" size="sm" disabled={census <= 0} onClick={() => setCensus((current) => Math.max(0, current - 1))}>
          One fewer
        </Button>
        <input type="number" min={0} max={max} aria-label="Census to try" className={INPUT} value={census} onChange={(event) => setCensus(Math.min(max, Math.max(0, Math.round(Number(event.target.value) || 0))))} />
        <Button icon={Plus} variant="secondary" size="sm" disabled={census >= max} onClick={() => setCensus((current) => Math.min(max, current + 1))}>
          One more
        </Button>
        <Button icon={ClipboardCheck} size="sm" disabled={record.isPending} onClick={() => record.mutate([{ unitId, date, census }], { onSuccess: onRecorded })}>
          {record.isPending ? 'Recording…' : `Record ${census} as ${date <= todayDate() ? 'the count' : 'expected'}`}
        </Button>
        <EndpointHint>GET /facilities/{'{facilityId}'}/census/requirements?date&unitId&census</EndpointHint>
        <EndpointHint>GET /facilities/{'{facilityId}'}/schedule/census/evaluation?date&unitId&census</EndpointHint>
      </div>
      {record.isError && <FormError error={record.error} fallback="The census could not be recorded." />}
      <div className="mt-2">
        {requirements.isPending ? <p className="text-[11px] text-ink-mid">Working it out…</p> : requirements.isError ? <LoadFailure size="xs" message="What it requires could not be worked out." onRetry={() => void requirements.refetch()} retrying={requirements.isFetching} /> : answer?.requirement ? <CensusRequirementView requirement={answer.requirement} showTraces configHref={configHref} /> : <p className="text-[11px] text-ink-mid">Nothing to require.</p>}
        {evaluation.isPending ? null : evaluation.isError ? <LoadFailure size="xs" className="mt-2" message="What it would change could not be worked out." onRetry={() => void evaluation.refetch()} retrying={evaluation.isFetching} /> : evaluation.data.evaluation ? <CensusEvaluationPanel evaluation={evaluation.data.evaluation} configHref={configHref} /> : null}
      </div>
    </div>
  )
}

/**
 * The Census tool (census response plan §4; the user's framing, 20 October
 * 2026: a facility administrator's daily tool, not a demo mode): today's
 * census per unit with Record, what today requires under the staffing plan,
 * the weeks around today as a grid of recorded counts and forecasts, and a
 * What-if on any cell. The partner's EHR feed writes the same records.
 */
/** The grid's reach: the weeks around today, or one whole month - this one, the next or the one after, so a released month further out can be worked. */
type GridChoice = 'around_today' | string

function rangeOf(choice: GridChoice, today: string): { from: string; to: string } {
  if (choice === 'around_today') {
    return censusRange(today)
  }
  const dates = datesOf(choice)
  return { from: dates[0] ?? `${choice}-01`, to: dates[dates.length - 1] ?? `${choice}-28` }
}

interface CensusCardProps {
  facilityId: string
  /** This facility's Configuration, for the way forward while it lists no units. */
  configHref?: string
}

export function CensusCard({ facilityId, configHref }: CensusCardProps) {
  const today = todayDate()
  const [grid, setGrid] = useState<GridChoice>('around_today')
  const range = rangeOf(grid, today)
  const query = useCensus(facilityId, range)
  const record = useRecordCensus(facilityId)
  const todayRequirements = useCensusRequirements(facilityId, { date: today })
  const [draft, setDraft] = useState<TodayDraft | null>(null)
  const [recorded, setRecorded] = useState<number | null>(null)
  const [whatIf, setWhatIf] = useState<{ date: string; unitId: string } | null>(null)
  const view = query.data ?? null
  const shown = draft ?? (view ? draftFrom(view, today) : {})

  const entries = view ? view.units.flatMap((unit) => {
    const census = parseCount(shown[unit.id] ?? '')
    const current = rowFor(view, today, unit.id)
    const unchanged = current && current.census !== null && current.basis === 'recorded' && current.census === census
    return census === null || unchanged ? [] : [{ unitId: unit.id, date: today, census }]
  }) : []

  // Every unit already holds today's recorded count, so there is nothing to record until one is changed.
  const allRecorded = view !== null && view.units.length > 0 && view.units.every((unit) => {
    const row = rowFor(view, today, unit.id)
    return row !== null && row.census !== null && row.basis === 'recorded' && row.census === parseCount(shown[unit.id] ?? '')
  })

  function submit(): void {
    record.mutate(entries, {
      onSuccess: (result) => {
        setRecorded(result.written)
        setDraft(null)
      },
    })
  }

  return (
    <div className="flex flex-col gap-4 rounded border border-marine/15 bg-white p-5">
      <div>
        <div className="flex items-center gap-1 text-sm font-black text-ink">
          Census
          <HelpIcon topic="schedule.census" />
          <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/census</EndpointHint>
          <EndpointHint className="ml-2">PUT /facilities/{'{facilityId}'}/census</EndpointHint>
        </div>
        <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">The number of residents on each unit, by day - a count only, never a resident. A day’s census is its PPD (patient days), and the staffing plan turns it into the seats each shift needs at the facility’s HPPD target. Record today’s count each morning; the weeks ahead carry what you expect or, where nothing is recorded, a forecast from the same weekday’s recent counts.</p>
      </div>
      {query.isPending ? (
        <Skeleton />
      ) : query.isError || !view ? (
        <LoadFailure size="xs" message="The census could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <>
          <section>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-marine">Today’s census · {shortDay(today)}</div>
            {view.units.length === 0 ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-[11px] text-ink-mid">No units are listed for this facility, so there is nowhere to record a census and the staffing plan requires nothing. List the units, with their beds and planned census, under Configuration › Details; then set the staffing plan and record the census here.</p>
                {configHref && (
                  <Link to={`${configHref}?tab=details`} className={buttonClass({ size: 'sm' })}>
                    <ButtonGlyph icon={Building2} size="sm" />
                    Set up the units
                  </Link>
                )}
              </div>
            ) : (
              <form
                className="divide-y divide-marine/10"
                onSubmit={(event) => {
                  event.preventDefault()
                  submit()
                }}
              >
                {view.units.map((unit) => (
                  <TodayRow key={unit.id} unit={unit} view={view} today={today} value={shown[unit.id] ?? ''} onChange={(text) => { setDraft({ ...shown, [unit.id]: text }); setRecorded(null) }} />
                ))}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button type="submit" icon={ClipboardCheck} size="sm" disabled={entries.length === 0 || record.isPending}>
                    {record.isPending ? 'Recording…' : entries.length > 0 ? `Record ${entries.length === 1 ? 'the count' : `${entries.length} counts`}` : allRecorded ? 'Today’s census is recorded' : 'Type today’s count to record'}
                  </Button>
                  {recorded !== null && <span className="text-[11px] text-green">Recorded {recorded === 1 ? 'the count' : `${recorded} counts`}.</span>}
                  {record.isError && <FormError error={record.error} fallback="The census could not be recorded." />}
                </div>
              </form>
            )}
          </section>

          {view.units.length > 0 && (
            <section>
              <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-marine">
                What today requires
                <EndpointHint>GET /facilities/{'{facilityId}'}/census/requirements?date</EndpointHint>
              </div>
              {todayRequirements.isPending ? (
                <p className="text-[11px] text-ink-mid">Working it out…</p>
              ) : todayRequirements.isError ? (
                <LoadFailure size="xs" message="What today requires could not be worked out." onRetry={() => void todayRequirements.refetch()} retrying={todayRequirements.isFetching} />
              ) : (
                <ul className="divide-y divide-marine/10">
                  {todayRequirements.data.units.map(({ unit, figure, requirement }) => (
                    <li key={unit.id} className="py-1.5">
                      <div className="text-xs font-bold text-ink">
                        {unit.name} <span className="font-normal text-ink-mid">· {figure ? `census ${figure.census}, ${describeRequirementFigure(figure, today)}` : describeRequirementFigure(null, today)}</span>
                      </div>
                      {requirement && <CensusRequirementView requirement={requirement} configHref={configHref} />}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <CensusDecisions facilityId={facilityId} thisMonth={today.slice(0, 7)} nextMonth={shiftMonth(today.slice(0, 7), 1)} canDecide />

          <section>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-marine">{grid === 'around_today' ? 'Two weeks back, four ahead' : 'The whole month'}</div>
              <MonthStepper value={grid} choices={['around_today', today.slice(0, 7), shiftMonth(today.slice(0, 7), 1), shiftMonth(today.slice(0, 7), 2)]} labelOf={(choice) => (choice === 'around_today' ? 'Around today' : describePeriod(choice))} onChange={(choice) => { setGrid(choice); setWhatIf(null) }} />
            </div>
            <p className="mb-2 text-[11px] text-ink-mid">Recorded counts in bold, forecasts in grey (the same weekday’s average over {view.forecastWeeks} weeks, else the unit’s planned census, capped at its beds). Tap a cell to try a different count and see what it would require and, on a released month, what it would change; then record it.</p>
            {whatIf && <div className="mb-3"><WhatIf facilityId={facilityId} view={view} date={whatIf.date} unitId={whatIf.unitId} onClose={() => setWhatIf(null)} onRecorded={() => setWhatIf(null)} configHref={configHref} /></div>}
            <CensusTable view={view} today={today} selected={whatIf} onSelect={(date, unitId) => setWhatIf({ date, unitId })} />
          </section>
        </>
      )}
    </div>
  )
}
